// Работа с Telegram Mini App: определить, что игра открыта внутри Telegram, и войти по `initData`.
//
// Игра — обычный сайт; внутри Telegram он открывается во встроенном браузере, а объект
// `window.Telegram.WebApp` появляется из скрипта `telegram-web-app.js` (подключён в `public/index.html`
// с сервера Telegram — самохостинг ломается на каждом обновлении Bot API).
//
// 🔴 ВНЕ TELEGRAM ЭТОТ МОДУЛЬ ОБЯЗАН БЫТЬ БЕЗВРЕДНЫМ: объекта нет → `isTelegram()` = false, и весь
// прежний путь (форма логина, cookie, пароль) работает как работал.

import { applyAuthHeader, clearJwt, clearLegacyToken, setJwt } from '@/session';

/** Объект мини-аппа или null (сайт открыт в обычном браузере). */
export function tg() {
  const api = (window.Telegram || {}).WebApp;
  return api && typeof api === 'object' ? api : null;
}

/** Игра открыта внутри Telegram? Признак — наличие подписанной строки `initData`. */
export function isTelegram() {
  const api = tg();
  return !!(api && api.initData);
}

/** Настольный клиент Telegram (Windows/macOS/Linux), где мини-апп живёт в ОТДЕЛЬНОМ окне. */
export function isDesktopTelegram() {
  const api = tg();
  const platform = String((api && api.platform) || '').toLowerCase();
  return platform === 'tdesktop' || platform === 'macos' || platform === 'linux';
}

/** Разово подготовить окно мини-аппа: сообщить о готовности и развернуть на всю высоту. */
export function prepareViewport() {
  const api = tg();
  if (!api) return;
  try {
    if (typeof api.ready === 'function') api.ready();
    // 🔴 `expand()` — ТОЛЬКО на мобильных и в веб-версии Telegram: там мини-апп это шторка, и вызов
    // раскрывает её на всю высоту. В НАСТОЛЬНОМ клиенте мини-апп открыт отдельным окном, и тот же
    // вызов растягивает ОКНО на максимум — у игрока с двумя мониторами оно занимало оба экрана
    // (жалоба 2026-08-20; из чата окно открывалось нормально, а по кнопке в списке чатов — во весь
    // рабочий стол). Размер окна на десктопе выбирает пользователь, игре там разворачивать нечего.
    if (!isDesktopTelegram() && typeof api.expand === 'function') api.expand();
    // Свайп вниз сворачивает мини-апп → рвётся сокет → партия засчитывается по неактивности.
    // Метод появился в Bot API 7.7; на клиентах постарше его просто нет.
    if (typeof api.disableVerticalSwipes === 'function') api.disableVerticalSwipes();
  } catch (e) { /* косметика не должна ронять запуск игры */ }
}

/** Реферальный код из ссылки-приглашения `t.me/<бот>/<имя>?startapp=КОД` (или null). */
export function startParam() {
  const api = tg();
  const raw = api && api.initDataUnsafe ? api.initDataUnsafe.start_param : null;
  const code = String(raw || '').trim();
  return code && /^[A-Za-z0-9_-]{1,64}$/.test(code) ? code : null;
}

// ── ОПЛАТА ЗВЁЗДАМИ (касса Stars, `BackEnd/docs/STARS_SHOP_PLAN.md`) ─────────────────────────────
// 🔴 ОКНО ОПЛАТЫ ОТКРЫВАЕТ ТОЛЬКО МИНИ-АПП. `openInvoice` — метод `Telegram.WebApp`, в обычном
// браузере его нет ни в каком виде: заплатить звёздами оттуда невозможно в принципе. Поэтому экран
// магазина ВНЕ Telegram обязан не рисовать кнопку покупки, а объяснять, где покупать, — иначе игрок
// жмёт мёртвую кнопку и считает, что магазин сломан.

/** Мини-апп умеет открывать счёт на оплату звёздами (Bot API 6.1+)? */
export function canOpenInvoice() {
  const api = tg();
  return !!(api && typeof api.openInvoice === 'function');
}

/**
 * Открыть счёт и дождаться, чем кончилось. Возвращает статус Telegram:
 * `paid` | `cancelled` | `failed` | `pending` (или `unsupported`, если метода нет).
 *
 * 🔴 `paid` — ЭТО НЕ ВЫДАЧА ТОВАРА. Telegram говорит только «деньги списаны»; товар выдаёт наш
 * сервер, когда до него доедет апдейт `successful_payment` через бота-шлюза, и это занимает секунды,
 * а бывает и дольше. Обещать игроку набор по этому статусу нельзя — ровно та же грабля, что с
 * подписью в кошельке TON («подпись ≠ зачисление»): экран обязан ДОЖДАТЬСЯ записи о покупке.
 * 🔴 `pending` — оплата принята к обработке, но статуса ещё нет: для нас это то же ожидание, что и
 * `paid`, а не отказ.
 */
export function openInvoice(link) {
  const api = tg();
  if (!canOpenInvoice()) return Promise.resolve('unsupported');
  return new Promise((resolve) => {
    let done = false;
    const finish = (status) => { if (!done) { done = true; resolve(String(status || 'failed')); } };
    try {
      api.openInvoice(link, finish);
    } catch (e) {
      finish('failed');
    }
  });
}

/** Идентификатор ТЕКУЩЕГО телеграм-пользователя (или null вне мини-аппа). */
export function telegramUserId() {
  const api = tg();
  const id = ((api || {}).initDataUnsafe || {}).user || {};
  const n = Number(id.id || 0);
  return n > 0 ? n : null;
}

// 🔴 ЧЕЙ ЭТО СОХРАНЁННЫЙ ТОКЕН (баг оунера 2026-08-15): на одном телефоне игрок переключил
// ТЕЛЕГРАМ-аккаунт и снова открыл мини-апп — а игра вошла под ПЕРВЫМ игроком, потому что при старте
// сохранённый JWT всегда имел приоритет над `initData`. Рядом с токеном запоминаем telegram_id, под
// которым он выдан: не совпал с текущим — токен чужой, входим заново по подписи Telegram.
const TG_OWNER_KEY = 'cz_tg_owner';

/** Запомнить, какому телеграм-аккаунту принадлежит текущая сессия. */
export function rememberTelegramOwner(id) {
  try {
    const val = Number(id || telegramUserId() || 0);
    if (val > 0) window.localStorage.setItem(TG_OWNER_KEY, String(val));
  } catch (e) { /* хранилище запрещено — просто не запоминаем */ }
}

/** Забыть владельца сессии (выход из аккаунта). */
export function forgetTelegramOwner() {
  try { window.localStorage.removeItem(TG_OWNER_KEY); } catch (e) { /* нет хранилища */ }
}

/**
 * Сохранённая сессия принадлежит ДРУГОМУ телеграм-аккаунту?
 *
 * true — токен выдан другому телеграм-пользователю (на телефоне переключили аккаунт): использовать
 * его нельзя, надо входить по свежему `initData`. Вне мини-аппа всегда false: там телеграм ни при чём.
 */
export function telegramOwnerChanged() {
  const now = telegramUserId();
  if (!now) return false;
  let saved = null;
  try { saved = window.localStorage.getItem(TG_OWNER_KEY); } catch (e) { saved = null; }
  if (!saved) return false;  // сессия заведена вне мини-аппа (вход паролем) — не трогаем
  return String(saved) !== String(now);
}

/**
 * Выбросить сохранённую сессию, если она заведена ДРУГИМ телеграм-аккаунтом.
 *
 * Зовётся при старте приложения ДО чтения токена: иначе игра войдёт под прежним игроком, потому что
 * сохранённый JWT приоритетнее `initData`. Возвращает true, если сессия была очищена.
 */
export function clearJwtIfForeignTelegram() {
  if (!telegramOwnerChanged()) return false;
  clearJwt();
  clearLegacyToken();
  forgetTelegramOwner();
  return true;
}

/** Язык интерфейса Telegram (`ru`, `en`, …) — точнее, чем язык браузера в WebView. */
export function telegramLocale() {
  const api = tg();
  const raw = ((api || {}).initDataUnsafe || {}).user || {};
  return String(raw.language_code || '').slice(0, 2).toLowerCase() || null;
}

/**
 * Войти по телеграм-аккаунту: шлём подписанную строку на сервер и получаем наш обычный JWT.
 *
 * Возвращает данные игрока или null, если войти не удалось (тогда экран показывает обычную форму
 * логина — молчаливый белый экран недопустим).
 *
 * 🔴 РЕФ-КОД ПЕРЕДАЁМ ВМЕСТЕ С ПЕРВЫМ ВХОДОМ: привязка к пригласившему возможна ТОЛЬКО в момент
 * создания игрока, второго шанса нет.
 */
export async function telegramLogin(axiosInstance, opts = {}) {
  const api = tg();
  if (!api || !api.initData) return null;
  const ref = startParam() || window.localStorage.getItem('cz_ref_code') || undefined;
  try {
    const body = { init_data: api.initData, ref };
    // 🔴 `create: false` — ПРОБА «сервер меня знает?» без регистрации. Первый запуск мини-аппа раньше
    // молча заводил новый аккаунт, и игрок со старым аккаунтом получал дубль, ничего не нажав.
    if (opts.create === false) body.create = false;
    const { data } = await axiosInstance.post('/users/telegram', body);
    if (data && data.exists === false) return { exists: false }; // такого игрока нет — решает человек
    const jwt = data.access_token || data.jwt;
    if (!jwt) return null;
    setJwt(jwt);
    applyAuthHeader(axiosInstance, jwt);
    rememberTelegramOwner();  // эта сессия принадлежит ЭТОМУ телеграм-аккаунту
    if (ref) window.localStorage.removeItem('cz_ref_code'); // использовали — больше не привязываем
    return data;
  } catch (e) {
    return null;
  }
}

/**
 * Привязать этот телеграм к УЖЕ открытому аккаунту (игрок вошёл логином и паролем).
 *
 * `takeover` — игрок ЯВНО просит перевесить свой Telegram сюда с телеграм-аккаунта, который создал ему
 * первый запуск мини-аппа. Сервер разрешает это только для аккаунта БЕЗ пароля (в него иначе не войти),
 * чужой аккаунт с паролем так не отобрать.
 */
export async function telegramLink(axiosInstance, opts = {}) {
  const api = tg();
  if (!api || !api.initData) throw new Error('not in telegram');
  const body = { init_data: api.initData };
  if (opts.takeover) body.takeover = true;
  const { data } = await axiosInstance.post('/users/telegram/link', body);
  rememberTelegramOwner();  // аккаунт привязан к этому телеграму — сессия его же
  return data;
}

/** Отвязать телеграм от текущего аккаунта (игрок сам, из профиля). */
export async function telegramUnlink(axiosInstance) {
  const { data } = await axiosInstance.delete('/users/telegram/link');
  return data;
}

// ── ПРИВЯЗКА TELEGRAM С ОБЫЧНОГО САЙТА — ЧЕРЕЗ МИНИ-ПРИЛОЖЕНИЕ ───────────────────────────────────
// 🔴 ЗАЧЕМ ОТДЕЛЬНЫЙ ПУТЬ: игрок сидит в браузере, мини-аппа нет — взять `initData` неоткуда.
// 🔴 TELEGRAM LOGIN WIDGET УБРАН (решение оунера 2026-08-18): его кнопку рисует сам Telegram, и у
// игроков она часто не появлялась вовсе — нет `/setdomain` у бота, другое написание домена, блокировщик
// скриптов. Ссылка в мини-апп открывает тот же Telegram игрока и ни от чего этого не зависит, поэтому
// путь остался один. Серверный роут `/users/telegram/link_widget` жив для вкладок на старой сборке.

/** Конфиг привязки с сайта: `{ bot, app, code_link }`. Ошибка/старый сервер → кнопку не показываем. */
export async function telegramLinkConfig(axiosInstance) {
  try {
    const { data } = await axiosInstance.get('/users/telegram/config');
    return data || null;
  } catch (e) {
    return null;
  }
}

// ── КАК ЭТО РАБОТАЕТ: одноразовый код + ссылка в мини-апп ─────────────────────────────────────────
// Сайт выдаёт код, игрок открывает по ссылке мини-апп, тот присылает код вместе с `initData` — сервер
// знает, к какому аккаунту привязывать телеграм. Ни домен сайта, ни сторонние скрипты в этом не
// участвуют, поэтому путь работает там, где кнопка-виджет молчала.

/** Взять одноразовый код привязки: `{ code, url, expires_in }`. */
export async function telegramLinkCode(axiosInstance) {
  const { data } = await axiosInstance.post('/users/telegram/link_code', {});
  return data;
}

/** Код привязки из ссылки запуска мини-аппа (`?startapp=link_КОД`) или пустая строка. */
export function linkCodeFromStart() {
  const p = startParam();
  return p && p.startsWith('link_') ? p.slice(5) : '';
}

/**
 * Дождаться подписанной строки `initData` (до `ms` миллисекунд).
 *
 * 🔴 Объект мини-аппа появляется раньше, чем заполняется `initData` (скрипт Telegram досоздаёт его
 * на старте). Ссылка привязки открывает мини-апп «с нуля», и запрос, ушедший в эту щель, летел бы
 * БЕЗ подписи — игрок получил бы отказ на ровном месте.
 */
export async function waitForInitData(ms = 3000) {
  const step = 150;
  for (let waited = 0; waited <= ms; waited += step) {
    const api = tg();
    if (api && api.initData) return api.initData;
    await new Promise((resolve) => setTimeout(resolve, step));
  }
  const api = tg();
  return (api && api.initData) || '';
}

/** Применить код привязки внутри мини-аппа: личность берём из `initData`, аккаунт — из кода. */
export async function telegramLinkByCode(axiosInstance, code) {
  if (!code) return null;
  const initData = await waitForInitData();
  if (!initData) return null;
  const { data } = await axiosInstance.post('/users/telegram/link_by_code', { init_data: initData, code });
  return data;
}


// ── ВОЛНА 2: ЭКРАН МИНИ-АППА (фуллскрин, безопасные зоны, кнопка «Назад», цвет шапки) ──────────
// Bot API 8.0 дал мини-аппам собственный полноэкранный режим. 🔴 ОБЫЧНЫЙ `element.requestFullscreen()`
// ВНУТРИ TELEGRAM НЕ РАБОТАЕТ (WebView запрещает его политикой) — промис молча отклоняется, и кнопка
// «во весь экран» выглядела мёртвой (жалоба оунера с телефона 2026-08-14). Поэтому в мини-аппе зовём
// телеграмный API, а на клиентах без 8.0 остаётся наш прежний фейк-фуллскрин (`fakeFs`).

/** Полноэкранный режим мини-аппа доступен (Bot API 8.0+)? */
export function canTelegramFullscreen() {
  const api = tg();
  return !!(api && typeof api.requestFullscreen === 'function');
}

/** Мини-апп сейчас развёрнут на весь экран. */
export function isTelegramFullscreen() {
  const api = tg();
  return !!(api && api.isFullscreen);
}

/**
 * Включить/выключить полноэкранный режим мини-аппа. Возвращает false, если API нет —
 * вызывающий тогда падает на прежний фейк-фуллскрин.
 */
export function toggleTelegramFullscreen() {
  const api = tg();
  if (!canTelegramFullscreen()) return false;
  try {
    if (api.isFullscreen) api.exitFullscreen();
    else api.requestFullscreen();
  } catch (e) {
    return false;
  }
  return true;
}

/** Подписка на смену/отказ полноэкранного режима. Возвращает функцию отписки. */
export function onTelegramFullscreen(onChange, onFailed) {
  const api = tg();
  if (!api || typeof api.onEvent !== 'function') return () => {};
  const change = () => onChange && onChange(!!api.isFullscreen);
  const failed = (e) => onFailed && onFailed(e);
  api.onEvent('fullscreenChanged', change);
  api.onEvent('fullscreenFailed', failed);
  return () => {
    try {
      api.offEvent('fullscreenChanged', change);
      api.offEvent('fullscreenFailed', failed);
    } catch (e) { /* клиент постарше — событий нет */ }
  };
}

/**
 * Безопасные зоны в CSS-переменные `--tg-safe-*`.
 *
 * 🔴 БЕЗ НИХ ПОЛНОЭКРАННЫЙ РЕЖИМ ПРЯЧЕТ ИНТЕРФЕЙС: контент уходит ПОД системную панель и под шапку
 * Telegram с кнопками «свернуть/закрыть», то есть таймеры и кнопки партии оказываются недоступны.
 * `safeAreaInset` — вырезы устройства (чёлка), `contentSafeAreaInset` — шапка самого Telegram;
 * складываем их, потому что перекрываются они оба.
 */
export function applySafeArea() {
  const api = tg();
  const root = document.documentElement;
  const dev = (api && api.safeAreaInset) || {};
  const con = (api && api.contentSafeAreaInset) || {};
  let changed = false;
  ['top', 'right', 'bottom', 'left'].forEach((side) => {
    const px = `${Number(dev[side] || 0) + Number(con[side] || 0)}px`;
    if (root.style.getPropertyValue(`--tg-safe-${side}`) !== px) changed = true;
    root.style.setProperty(`--tg-safe-${side}`, px);
  });
  // 🔴 ЗОНЫ ИЗМЕНИЛИСЬ — ДЁРГАЕМ `resize`: размер доски в партии считается в computed, а CSS-переменную
  // Vue не отслеживает. Без этого события доска после входа в полноэкранный режим оставалась посчитанной
  // по старым отступам (верхний ряд под шапкой Telegram) до первого поворота экрана.
  if (changed && typeof window !== 'undefined') window.dispatchEvent(new Event('resize'));
}

/** Следить за изменением безопасных зон (поворот, вход/выход из фуллскрина). */
export function watchSafeArea() {
  const api = tg();
  applySafeArea();
  if (!api || typeof api.onEvent !== 'function') return;
  ['safeAreaChanged', 'contentSafeAreaChanged', 'viewportChanged', 'fullscreenChanged'].forEach((ev) => {
    try { api.onEvent(ev, applySafeArea); } catch (e) { /* событие не поддержано клиентом */ }
  });
}

/**
 * Кнопка «Назад» самого Telegram вместо нашей: показываем везде, кроме главного экрана.
 * `goBack` — та же функция, что у кнопки в интерфейсе игры (одно поведение на оба пути).
 */
export function setupBackButton(router, goBack) {
  const api = tg();
  const btn = api && api.BackButton;
  if (!btn || typeof btn.onClick !== 'function') return;
  try {
    btn.onClick(() => goBack());
    const sync = (route) => {
      const path = (route && route.path) || '/';
      if (path === '/') btn.hide(); else btn.show();
    };
    sync(router.currentRoute && router.currentRoute.value);
    router.afterEach((to) => sync(to));
  } catch (e) { /* клиент без BackButton — остаётся наша кнопка */ }
}

/** Шапка и фон Telegram под тёмную палитру игры (иначе над сценой висит светлая полоса). */
export function applyTheme(color = '#141019') {
  const api = tg();
  if (!api) return;
  try {
    if (typeof api.setHeaderColor === 'function') api.setHeaderColor(color);
    if (typeof api.setBackgroundColor === 'function') api.setBackgroundColor(color);
    if (typeof api.setBottomBarColor === 'function') api.setBottomBarColor(color);
  } catch (e) { /* цвет не поддержан — косметика, не блокер */ }
}
